import { useState, useMemo,useEffect } from 'react';
import PropTypes from 'prop-types';
import { useLocation, matchPath } from 'react-router-dom';

import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';

export default function NavCollapse({ item, level }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();
  const theme = useTheme();

  const isSelected = useMemo(
    () => item.children?.some((child) => !!matchPath({ path: child.url, end: false }, pathname)),
    [item.children, pathname]
  );

  const [open, setOpen] = useState(isSelected);

  useEffect(()=>{
    if (!drawerOpen) {
      setOpen(false);
    } else if (isSelected) {
      setOpen(true);
    }
  },[drawerOpen, isSelected])

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
  ) : null;

   
return (
    <>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        selected={isSelected}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          '&:hover': {
            bgcolor: theme.palette.primary.lighter,
            '& .MuiTypography-root': { color: 'primary.main' },
            '& .MuiListItemIcon-root': { color: 'primary.main' }
          },
          '&.Mui-selected': {
            bgcolor: theme.palette.primary.light,
            '& .MuiTypography-root': { color: 'primary.main' },
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            '&:hover': { bgcolor: theme.palette.primary.light }
          },
          ...(drawerOpen && {
            '&:hover': { bgcolor: theme.palette.primary.lighter },
            '&.Mui-selected': {
              bgcolor: theme.palette.primary.light,
              borderRight: '2px solid',
              borderColor: 'primary.main'
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': {
              bgcolor: 'transparent',
              '&:hover': { bgcolor: 'transparent' }
            }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isSelected ? 'primary.main' : theme.palette.text.secondary,
              '.MuiListItemButton-root:hover &': { color: 'primary.main' },
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center'
              })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                variant="h6"
                sx={{
                  color: isSelected ? 'primary.main' : theme.palette.text.primary,
                  '.MuiListItemButton-root:hover &': { color: 'primary.main' }
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isSelected ? 'primary.main' : theme.palette.text.secondary,
              '.MuiListItemButton-root:hover &': { color: 'primary.main' }
            }}
          >
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </ListItemIcon>
        )}
      </ListItemButton>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{
            ml: drawerOpen ? `${level * 28}px` : 2,
            pl: drawerOpen ? 1 : 0
          }}>
            {item.children?.map((child) => (
              <NavItem key={child.id} item={child} level={level+1} subItem={true}/>
            ))}
          </List>
      </Collapse>

    </>
  );
}

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};